import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    computed,
    inject,
    signal,
    viewChild,
} from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { HttpErrorResponse } from "@angular/common/http";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { filter, finalize } from "rxjs";
import { ALERT_ACTION_LABELS, AlertAction, AlertsService } from "../alerts.service";
import { NotificationsService } from "../../../../../shared/_services/notifications.service";

export type AlertActionDialogData = {
    alertId: string;
    action: AlertAction;
    subject: string;
};

// The comment columns' size, which the API's CommentMaxLength setting may not exceed. A lower setting is refused
// with the API's own 400 answer, which the dialog shows.
export const COMMENT_MAX_LENGTH = 500;

const CONSEQUENCES: Record<AlertAction, string> = {
    [AlertAction.Acknowledge]:
        "You take ownership of this alert for the team. Reminders stop; repeats are still posted, less often.",
    [AlertAction.Resolve]: "The alert is closed. Its next occurrence starts a new alert.",
    [AlertAction.Ignore]:
        "Nothing more is posted about this Warning. It stays on this list as Ignored, counting repeats, until it goes quiet and resolves itself.",
};

const SUCCESS_MESSAGES: Record<AlertAction, string> = {
    [AlertAction.Acknowledge]: "Alert acknowledged",
    [AlertAction.Resolve]: "Alert resolved",
    [AlertAction.Ignore]: "Alert ignored",
};

// Closes with true when the page must reload: the action was saved, or the alert changed under it. It cannot be closed
// while the action is being sent: the API edits the Telegram message within the request, which can take seconds, and
// leaving would cancel the request and leave the page stale.
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "app-alert-action-dialog",
    templateUrl: "./alert-action-dialog.component.html",
    styleUrl: "./alert-action-dialog.component.scss",
    imports: [ReactiveFormsModule, MatDialogModule, MatButtonModule, MatFormFieldModule, MatInputModule],
})
export class AlertActionDialogComponent {
    readonly data = inject<AlertActionDialogData>(MAT_DIALOG_DATA);
    private readonly dialogRef = inject<MatDialogRef<AlertActionDialogComponent, boolean>>(MatDialogRef);
    private readonly alertsService = inject(AlertsService);
    private readonly notificationsService = inject(NotificationsService);
    private readonly fb = inject(FormBuilder);
    private readonly destroyRef = inject(DestroyRef);
    private readonly commentBox = viewChild.required<ElementRef<HTMLTextAreaElement>>("commentBox");

    readonly commentMaxLength = COMMENT_MAX_LENGTH;
    readonly actionLabel = ALERT_ACTION_LABELS[this.data.action];
    readonly consequence = CONSEQUENCES[this.data.action];

    readonly form = this.fb.nonNullable.group({
        comment: ["", Validators.maxLength(COMMENT_MAX_LENGTH)],
    });

    private readonly formStatus = toSignal(this.form.statusChanges, { initialValue: this.form.status });
    readonly isSending = signal(false);
    readonly canSubmit = computed(() => this.formStatus() === "VALID" && !this.isSending());
    readonly errorMessage = signal<string | null>(null);
    private mustReload = false;

    constructor() {
        // The overlay's own key events: Escape works wherever the focus is, as the dialog is opened with disableClose.
        this.dialogRef
            .keydownEvents()
            .pipe(
                filter((event) => event.key === "Escape"),
                takeUntilDestroyed(),
            )
            .subscribe(() => this.cancel());
    }

    submit(): void {
        if (!this.canSubmit()) {
            return;
        }

        const comment = this.form.controls.comment.value;
        this.isSending.set(true);
        this.errorMessage.set(null);
        this.alertsService
            .submitAction(this.data.alertId, this.data.action, comment.trim().length > 0 ? comment : null)
            .pipe(
                finalize(() => this.isSending.set(false)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: () => {
                    // On the list a resolved alert simply disappears, so the toast is the only confirmation.
                    this.notificationsService.showSuccess("Success", SUCCESS_MESSAGES[this.data.action]);
                    this.dialogRef.close(true);
                },
                error: (error: HttpErrorResponse) => this.handleActionError(error),
            });
    }

    cancel(): void {
        if (this.isSending()) {
            return;
        }
        this.dialogRef.close(this.mustReload);
    }

    private handleActionError(error: HttpErrorResponse): void {
        // A 409 means another admin, a repeat or a timer changed the alert since the page was loaded.
        if (error.status === 409 || error.status === 404) {
            this.mustReload = true;
        }
        this.errorMessage.set(errorMessageOf(error));
        // The disabled submit button had the focus; the comment is where the admin goes next.
        this.commentBox().nativeElement.focus();
    }
}

// The API's 409 and 400 answers are plain text written for the admin.
function errorMessageOf(error: HttpErrorResponse): string {
    if ((error.status === 409 || error.status === 400) && typeof error.error === "string" && error.error.length > 0) {
        return error.error;
    }
    if (error.status === 404) {
        return "Alert not found";
    }
    return "The action failed. Try again.";
}
